import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { 
  User, 
  UserRole, 
  login as apiLogin, 
  logout as apiLogout, 
  getCurrentUser, 
  verifyToken,
  hasRole,
  getRoleDashboardUrl
} from '../Services/AuthService';

// Interfaz para el contexto de autenticación
interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string, onSuccess?: (user: User) => void) => Promise<void>;
  logout: () => void;
  error: string | null;
  hasRole: (minimumRole: UserRole) => boolean;
  getRoleDashboardUrl: (role: UserRole) => string;
}

// Valor por defecto para el contexto
const defaultAuthContext: AuthContextType = {
  user: null,
  isLoading: true,
  login: async () => {},
  logout: () => {},
  error: null,
  hasRole: () => false,
  getRoleDashboardUrl: () => ''
};

// Crear el contexto
const AuthContext = createContext<AuthContextType>(defaultAuthContext);

// Interfaz para las props del proveedor
interface AuthProviderProps {
  children: ReactNode;
}

// Componente proveedor que envolverá la aplicación
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Verificar el estado de autenticación al cargar la aplicación
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      
      try {
        // Intentar obtener el usuario del localStorage
        const currentUser = getCurrentUser();
        
        if (currentUser) {
          // Verificar si el token sigue siendo válido
          const isValid = await verifyToken();
          
          if (isValid) {
            setUser(currentUser);
          } else {
            // Si el token no es válido, limpiar el estado
            setUser(null);
          }
        }
      } catch (err) {
        console.error('Error al inicializar la autenticación:', err);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // Función para iniciar sesión
  const login = async (email: string, password: string, onSuccess?: (user: User) => void) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const userData = await apiLogin(email, password);
      setUser(userData);
      
      // Ejecutar callback de éxito si se proporciona
      if (onSuccess) {
        onSuccess(userData);
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        // Manejar respuestas de error específicas
        if (err.response.status === 401) {
          setError('Credenciales incorrectas');
        } else {
          setError('Error durante el inicio de sesión. Por favor, inténtalo de nuevo.');
        }
      } else {
        setError('Error al conectar con el servidor');
      }
      console.error('Error de login:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para cerrar sesión
  const logout = async () => {
    await apiLogout();
    setUser(null);
  };

  // Función para verificar si el usuario tiene un rol específico
  const checkRole = (minimumRole: UserRole): boolean => {
    return hasRole(user, minimumRole);
  };

  // Valor que se proporcionará a los consumidores del contexto
  const value = {
    user,
    isLoading,
    login,
    logout,
    error,
    hasRole: checkRole,
    getRoleDashboardUrl
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook personalizado para usar el contexto de autenticación
export const useAuth = () => useContext(AuthContext);